import { FC, useRef, useState } from 'react';
import {
  useDeleteSellerRequestMutation,
  useRejectDealDocumentMutation,
  useSignDealDocumentMutation,
} from '@/app/api';
import { Box, Button, Grid, Stack, SxProps, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { eimzo } from '../../../entities/eimzo';
import { CommentModal } from '../../modals/comment-modal';
import { useNotification } from '../../contexts/NotificationContext';
import { getFetchEror, triada } from '../../../app/helpers';

import { useTranslation } from 'react-i18next';
import { DealRequest } from '@/app/api/types';
import { CommentModalRef } from '../../modals/comment-modal/types';
import { FactorAddDocument } from '@/widgets/factor-add-document';
import {
  useLazyGetDocBase64Query,
  useLazyGetDocQuery,
  useLazyGetStringToSignQuery,
} from '@/entities/didox/api';
import { useAppSelector } from '@/app/hooks';
import { UIDefinitionList } from '@/shared/ui/ui-definition-list';
import {
  checkIsActorSignPending,
  getDocsPendingActorSign,
} from '../lib/get-single-deal-status';

interface Props {
  data: DealRequest;
  type: 'seller' | 'buyer' | 'factor';
  sx?: SxProps;
  isDisabled: boolean;
}

export const SingleDealActions: FC<Props> = ({ data, type, sx }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { profile, isUsbLog } = useAppSelector((state) => state.auth);

  const [isSignDocLoading, setIsSignDocLoading] = useState(false);
  const [isRejectDocLoading, setIsRejectDocLoading] = useState(false);

  const commentModalRef = useRef<CommentModalRef>(null);

  const unique = data.unique;

  const [deleteDeal, { isLoading: isDeleteLoading }] =
    useDeleteSellerRequestMutation();
  const [signDocument] = useSignDealDocumentMutation();
  const [rejectDocument] = useRejectDealDocumentMutation();
  const [getDoc] = useLazyGetDocQuery();
  const [getToSign] = useLazyGetStringToSignQuery();
  const [getDocBase64] = useLazyGetDocBase64Query();

  const isClosedDeal = data?.factoringType === 'closed';
  const isReverseFactoring = data.factoringType === 'reverse';
  // International deals (export/import) have no debt registry — only the
  // factoring contract is signed. Treat them like closed deals here.
  const hasNoDebtRegistry = isClosedDeal || !!data.isInternational;
  const residentSignDocId = hasNoDebtRegistry
    ? data.factoringContract?.didoxId
    : data.debtRegistry?.didoxId;

  const isContractAndRegistrySent =
    data.debtRegistry &&
    data.factoringContract &&
    (data.status === 'waitingForFactorAcceptance' ||
      data.status === 'waitingForBuyerAcceptance' ||
      data.status === 'completed' ||
      data.status === 'contractCreated' ||
      data.status === 'contractCreatedByFactor');

  // * seller
  const handleDelete = async () => {
    try {
      await deleteDeal({ unique }).unwrap();
      showNotification(t('deal_successfully_deleted'), 'success');
    } catch (error) {
      showNotification(getFetchEror(error), 'error');
    }
  };

  const handleSignDoc = async (id: string) => {
    try {
      setIsSignDocLoading(true);

      const toSign = await getToSign({
        docId: id,
        action: 'partnerSign',
      })
        .unwrap()
        .then((res) => res.data);

      let signature = '';

      if (typeof toSign === 'string') {
        const useModern = await eimzo.isModernEimzoFlow(isUsbLog);
        const originalString = useModern
          ? await getDocBase64(id)
              .unwrap()
              .then((res) => res.data)
          : undefined;
        signature = await eimzo.attach(
          toSign,
          profile?.tin || '',
          undefined,
          isUsbLog,
          originalString
        );
      } else {
        signature = await eimzo.sign(
          JSON.stringify(toSign),
          profile?.tin || '',
          isUsbLog
        );
      }

      await signDocument({ id, signature }).unwrap();
      showNotification(t('document_successfully_signed'), 'success');
    } catch (error) {
      showNotification(getFetchEror(error), 'error');
    } finally {
      setIsSignDocLoading(false);
    }
  };

  const handleSignWithSeller = async () => {
    let chain = Promise.resolve();
    try {
      setIsSignDocLoading(true);
      const docIds = [
        data.factoringContract?.didoxId,
        data.debtRegistry?.didoxId,
      ];
      if (hasNoDebtRegistry) {
        docIds.pop();
      }

      const docs = await Promise.all(
        docIds.map((id) =>
          getToSign({
            docId: id || '',
            action: 'partnerSign',
          })
            .unwrap()
            .then((res) => res.data)
        )
      );

      const toSignDocs = [data.factoringContract, data.debtRegistry];

      docs.forEach((el, index) => {
        chain = chain
          .then(async () => {
            let signature = '';
            if (typeof el === 'string') {
              const docId = toSignDocs[index]?.didoxId || '';
              const useModern = await eimzo.isModernEimzoFlow(isUsbLog);
              const originalString = useModern
                ? await getDocBase64(docId)
                    .unwrap()
                    .then((res) => res.data)
                : undefined;
              signature = await eimzo.attach(
                el,
                profile?.tin || '',
                undefined,
                isUsbLog,
                originalString
              );
            } else {
              signature = await eimzo.sign(
                JSON.stringify(el),
                profile?.tin || '',
                isUsbLog
              );
            }

            await signDocument({
              id: toSignDocs[index]?.didoxId || '',
              signature,
            }).unwrap();
          })
          .finally(() => {
            if (index === docs.length - 1) {
              setIsSignDocLoading(false);
              showNotification(t('document_successfully_signed'), 'success');
            }
          })
          .catch((error) => {
            showNotification(getFetchEror(error), 'error');
            setIsSignDocLoading(false);
          });
      });
    } catch (err) {
      showNotification(getFetchEror(err), 'error');
      setIsSignDocLoading(false);
    }
  };

  const handleRejectDocument = async (id: string) => {
    try {
      const comment = await commentModalRef.current?.open({
        title: t('reject_document'),
        submitBtnText: t('reject'),
      });
      if (comment) {
        setIsRejectDocLoading(true);
        const toSign = await getToSign({
          docId: id,
          action: 'reject',
          comment,
        }).unwrap();
        const signature = await eimzo.sign(
          JSON.stringify(toSign.data),
          profile?.tin || '',
          isUsbLog
        );
        await rejectDocument({ id, signature, comment }).unwrap();
        showNotification(t('document_successfully_rejected'), 'success');
      }
    } catch (error) {
      showNotification(getFetchEror(error), 'error');
    } finally {
      setIsRejectDocLoading(false);
    }
  };

  const handleRejectDocsWithSeller = async () => {
    let chain = Promise.resolve();
    try {
      setIsRejectDocLoading(true);
      const docIds = [
        data.factoringContract?.didoxId,
        data.debtRegistry?.didoxId,
      ];
      if (hasNoDebtRegistry) {
        docIds.pop();
      }

      const docs = await Promise.all(
        docIds.map((id) => getDoc(id || '').unwrap())
      );

      const toSignDocs = [data.factoringContract, data.debtRegistry];

      const comment = await commentModalRef.current?.open({
        title: t('reject_document'),
        submitBtnText: t('reject'),
      });

      docs.forEach((_el, index) => {
        chain = chain
          .then(async () => {
            if (comment) {
              const toSign = await getToSign({
                docId: toSignDocs[index]?.didoxId || '',
                action: 'reject',
                comment,
              }).unwrap();
              const signature = await eimzo.sign(
                JSON.stringify(toSign.data),
                profile?.tin || '',
                isUsbLog
              );
              await rejectDocument({
                id: toSignDocs[index]?.didoxId || '',
                signature,
                comment,
              }).unwrap();
            }
          })
          .finally(() => {
            if (index === docs.length - 1) {
              setIsRejectDocLoading(false);
              showNotification(t('document_successfully_rejected'), 'success');
            }
          })
          .catch((error) => {
            showNotification(getFetchEror(error), 'error');
            setIsRejectDocLoading(false);
          });
      });
    } catch (err) {
      showNotification(getFetchEror(err), 'error');
      setIsRejectDocLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/${type}/deals`);
  };

  const isBuyerSigned =
    !!data.debtRegistry?.signers?.find((el) => el.actor === 'buyer')
      ?.signedAt || hasNoDebtRegistry;

  // Единый источник правды с чипом статуса: какие документы ждут подписи именно
  // этого актора (учитывает реверс и двусторонний договор).
  const isSignPending = checkIsActorSignPending(data, type);

  // По мигрированной реверсивной сделке часть документов актор мог подписать
  // раньше - тогда подписываем/отклоняем только оставшийся.
  const pendingDocs = getDocsPendingActorSign(data, type);
  const reverseSinglePendingDocId =
    isReverseFactoring && pendingDocs.length === 1
      ? pendingDocs[0]?.didoxId
      : undefined;

  const renderButtons = () => {
    switch (type) {
      case 'buyer':
        return (
          <>
            {isReverseFactoring && data.status == 'draft' && (
              <>
                <Button
                  color='inherit'
                  disabled={isDeleteLoading}
                  onClick={handleDelete}
                >
                  {t('delete_bid')}
                </Button>
                <Link to={`/buyer/deals/create/${data.unique}`}>
                  <Button color='inherit'>{t('edit')}</Button>
                </Link>
              </>
            )}
            <Link
              to={
                !isReverseFactoring
                  ? `/buyer/incoming-requests`
                  : `/seller/deals`
              }
            >
              <Button onClick={handleBack}>{t('close')}</Button>
            </Link>
          </>
        );
      case 'seller':
        return (
          <>
            {data.status === 'draft' && (
              <Button
                color='inherit'
                disabled={isDeleteLoading}
                onClick={handleDelete}
              >
                {t('delete_bid')}
              </Button>
            )}
            {data.status === 'draft' && (
              <Link to={`/seller/deals/create/${data.unique}`}>
                <Button color='inherit'>{t('edit')}</Button>
              </Link>
            )}
            {!(data.status === 'active' || data.status === 'underReview') && (
              <Link
                to={
                  isReverseFactoring
                    ? `/buyer/incoming-requests`
                    : `/seller/deals`
                }
              >
                <Button onClick={handleBack}>{t('close')}</Button>
              </Link>
            )}
          </>
        );
      default:
        return null;
    }
  };

  const handleReject = async () => {
    if (type === 'seller') {
      if (reverseSinglePendingDocId) {
        handleRejectDocument(reverseSinglePendingDocId);
      } else {
        handleRejectDocsWithSeller();
      }
    } else if (type === 'buyer') {
      if (reverseSinglePendingDocId) {
        handleRejectDocument(reverseSinglePendingDocId);
      } else if (isReverseFactoring) {
        handleRejectDocsWithSeller();
      } else {
        handleRejectDocument(residentSignDocId || '');
      }
    }
  };

  const handleSign = async () => {
    if (type === 'seller') {
      if (reverseSinglePendingDocId) {
        handleSignDoc(reverseSinglePendingDocId);
      } else {
        handleSignWithSeller();
      }
    } else if (type === 'buyer') {
      if (reverseSinglePendingDocId) {
        handleSignDoc(reverseSinglePendingDocId);
      } else if (isReverseFactoring || data.isTripartiteContract) {
        // await Promise.all([
        //   handleSignDoc(data.debtRegistry?.didoxId || ''),
        //   handleSignDoc(data.factoringContract?.didoxId || ''),
        // ]);
        handleSignWithSeller();
      } else {
        handleSignDoc(residentSignDocId || '');
      }
    }
  };

  const signBtnVisibility =
    type === 'seller'
      ? (data.status === 'waitContragentSign' ||
          data.status === 'contractCreated') &&
        isSignPending &&
        isBuyerSigned
      : (data.status === 'contractCreated' ||
          data.status === 'waitContragentSign') &&
        isSignPending;

  const rejectBtnVisibility =
    type === 'seller'
      ? (data.status === 'waitContragentSign' ||
          data.status === 'contractCreated') &&
        isSignPending &&
        isBuyerSigned
      : isSignPending && data.status === 'contractCreated';

  // const getRedeemPaymentSum = () => {
  //   const commissionType = data.comissionCalculationTypeId;
  //   const s = data.factorDealAmount;
  //   const d = data.factorDiscount;

  //   let repaymentAmount = 0;

  //   if (commissionType === 'insideTheAmount') {
  //     repaymentAmount = s;
  //   } else if (commissionType === 'onTopOfTheAmount') {
  //     repaymentAmount = s * (1 + d / 100);
  //   }

  //   return repaymentAmount;
  // };

  return (
    <Stack spacing={2} sx={sx}>
      {!isContractAndRegistrySent && (
        <Stack direction='row' spacing={2} justifyContent='flex-end' mb={2}>
          {renderButtons()}
        </Stack>
      )}
      <Box
        sx={{
          backgroundColor: 'rgba(13, 29, 88, 0.04)',
          p: 3,
          borderRadius: '12px',
        }}
      >
        <Typography variant='h5' sx={{ mb: 2.5 }}>
          {t('factors_reply')}
        </Typography>

        <Grid
          container
          sx={{
            '& dt': (theme) => ({
              color: theme.palette.primary.main,
            }),
          }}
        >
          <Grid item xs={12} md={6} lg={2}>
            <UIDefinitionList
              term={t('factoring_type')}
              description={data.recourseTypeName}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <UIDefinitionList
              term={t('deal_sum')}
              description={triada(data.factorDealAmount, { decimal: 2 })}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <UIDefinitionList
              term={t('discount_2') + ', %'}
              description={
                data.factorDiscount
                  ? `${data.factorDiscount} ${data.comissionCalculationTypeName || ''}`
                  : t('no_data')
              }
            />
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <UIDefinitionList
              term={t('pay_sum')}
              description={triada(data.factorPaymentAmount, { decimal: 2 })}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <UIDefinitionList
              term={t('pay_date')}
              description={data.factorPaymentDate}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <UIDefinitionList
              // term={t('redeem_sum')}
              term={t('commission_sum')}
              // description={triada(getRedeemPaymentSum(), { decimal: 2 })}
              description={triada(data.factorCommission, { decimal: 2 })}
            />
          </Grid>
        </Grid>

        <Stack spacing={1} mt={2.5}>
          {!!data.factoringContract?.name && (
            <FactorAddDocument
              documentType='contract'
              type={type}
              label={t('pin_factoring_contract')}
              value={data.factoringContract}
            />
          )}

          {!!data.debtRegistry?.name && (
            <FactorAddDocument
              documentType='registry'
              type={type}
              label={t('pin_factoring_contract')}
              value={data.debtRegistry}
            />
          )}
        </Stack>

        {(signBtnVisibility || rejectBtnVisibility) && (
          <Stack direction='row' spacing={2} justifyContent='flex-end' mt={2.5}>
            {rejectBtnVisibility && (
              <Button
                onClick={handleReject}
                color='error'
                disabled={isRejectDocLoading}
              >
                {t('reject')}
              </Button>
            )}
            {signBtnVisibility && (
              <Button onClick={handleSign} disabled={isSignDocLoading}>
                {t('sign')}
              </Button>
            )}
          </Stack>
        )}
      </Box>

      <CommentModal ref={commentModalRef} />
    </Stack>
  );
};
